import { flash, theme } from "@xola/ui-kit";
import { ValueGetterParams } from "ag-grid-enterprise";
import { AxiosInstance } from "axios";
import {
    convertAnyStringToFormattedTime,
    getLineItemByCode,
    isExperiencePurchaseItem,
    isGiftPurchaseItem,
    isPartnerPurchase,
    PurchaseItemStatus,
} from "common";
import { get } from "get-wild";
import { cloneDeep, isEmpty, pick, sumBy, uniq } from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
    ExperiencePurchaseItem,
    LineItemCode,
    LineItemStatus,
    LineItemType,
    PurchaseItem,
    PurchaseItemObject,
    Report,
    ReportObject,
} from "types";
import shallow from "zustand/shallow";
import { ColumnLayout } from "../../Layout/ColumnLayout";
import { CustomReportFilter } from "./CustomReportFilter";
import { CustomReportGrid, CustomReportGridRef } from "./CustomReportGrid";
import { ReportFilterOperator, ReportFilterState } from "./ReportFilterState";
import { ReportFilterTagList } from "./ReportFilterTagList";
import { ReportColumnDef, useSuggestedReports } from "./SuggestedReports";
import { useInfiniteCollection } from "@/hooks/useInfiniteCollection";
import { useResource } from "@/hooks/useResource";
import { formatCreatedAtEndDate, getPendingRangeFilterKey, getReportFilterState } from "@/models/Report";
import { getQueryParams } from "@/models/ReportFilterState";
import { isPartnerReconcileTransaction } from "@/models/Transaction";
import { useCustomReportStore } from "@/stores/customReportFilterStore";

const numberColumn = {
    headerName: "",
    field: "",
    pinned: true,
    minWidth: 50,
    maxWidth: 50,
    cellStyle: {
        backgroundColor: theme.colors.gray.lighter,
        color: theme.colors.gray.dark,
        textAlign: "center",
        padding: 0,
    },
    valueGetter: (params: ValueGetterParams) => {
        return (params.node?.rowIndex ?? 0) + 1;
    },
};

interface CustomReportProps {
    report: Report;
    columns: ReportColumnDef[];
    unwind?: (data: any[]) => any[];
    dateFields: string[];
    apiClient?: AxiosInstance;
    excludeFutureDates?: boolean;
}

export const CustomReport = ({
    report,
    columns,
    dateFields,
    apiClient,
    unwind,
    excludeFutureDates,
}: CustomReportProps) => {
    const history = useHistory();
    const ref = useRef<CustomReportGridRef>(null);

    const [excludedColumns, setExcludedColumns] = useState<string[]>(report.excludedColumns);

    const [initialFilter, setInitialFilter, currentFilter, setCurrentFilter, cleanFilter] = useCustomReportStore(
        (state) => [
            state.initialFilter,
            state.setInitialFilter,
            state.currentFilter,
            state.setCurrentFilter,
            state.cleanFilter,
        ],
        shallow,
    );

    useEffect(() => {
        setInitialFilter(getReportFilterState(report));
        setCurrentFilter(getReportFilterState(report));

        return () => cleanFilter();
        // we need to call in only on first render to set initial state
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [filter, setFilter] = useState<ReportFilterState>(getReportFilterState(report));
    const params = getQueryParams(filter);
    const suggestedReports = useSuggestedReports();

    const {
        data,
        isLoading,
        isFetching,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
        refetch: refetchReport,
    } = useInfiniteCollection(report.collection, params, {
        staleTime: 100,
        keepPreviousData: true,
        enabled: columns.length > 0,
        apiClient: apiClient,
    });

    const shouldFetchSummary = !!report?.showSummary && columns.length > 0;
    const {
        data: summary,
        refetch: refetchSummary,
        isFetching: isFetchingSummary,
    } = useResource<{ count: number }>([report.collection, "summary"], params, {
        staleTime: 100,
        enabled: shouldFetchSummary,
    });

    if (report.object === ReportObject.RESERVATIONS && data) {
        for (const experiencePurchase of data) {
            let totalRevenueForExperience = 0;
            const currentPurchaseItem = experiencePurchase as ExperiencePurchaseItem;

            if (currentPurchaseItem.revenue) continue;

            totalRevenueForExperience = sumBy(currentPurchaseItem.purchase.lineItems, (lineItem) => {
                const allowedLineItemTypes = [
                    LineItemType.PAYMENT,
                    LineItemType.REFUND,
                    LineItemType.AFFILIATE_DEPOSIT,
                ];
                return allowedLineItemTypes.includes(lineItem.type) && lineItem.status === LineItemStatus.COMPLETE
                    ? lineItem.amount * -1
                    : 0;
            });

            if (currentPurchaseItem.purchase.items && currentPurchaseItem.purchase.items.length > 1) {
                const purchaseItems = [];
                for (const itemId of currentPurchaseItem.purchase.items) {
                    const subPurchaseItem = data.find((item) => item.id === itemId.id) as PurchaseItem | undefined;
                    if (!subPurchaseItem) {
                        continue;
                    }

                    purchaseItems.push(subPurchaseItem);
                }

                purchaseItems.sort(sortPurchaseItems);

                for (const subPurchaseItem of purchaseItems) {
                    let itemRevenue = 0;

                    if (totalRevenueForExperience > 0 && subPurchaseItem.status >= PurchaseItemStatus.CONFIRMED) {
                        const partnerFee = subPurchaseItem.purchase.lineItems.find(
                            (line) => line.code === LineItemCode.PARTNER_FEES && line.includeInAmount,
                        );

                        const itemRevenueWithPartnerFee =
                            subPurchaseItem.amount + (partnerFee ? getPartnerFeeForItem(subPurchaseItem) : 0);

                        itemRevenue = Math.min(itemRevenueWithPartnerFee, totalRevenueForExperience);
                        totalRevenueForExperience -= itemRevenue;
                    }

                    subPurchaseItem.revenue = itemRevenue;
                }
            } else {
                currentPurchaseItem.revenue = totalRevenueForExperience;
            }
        }
    }

    const handleFilterChange = (filter: ReportFilterState, keyToBeRemoved?: keyof ReportFilterState) => {
        const localFilter = { ...filter };

        if (localFilter.product?.operator === ReportFilterOperator.IN && isEmpty(localFilter.product?.value)) {
            // Hack for removing "product[in]=[]" from the query params because Rushi can't figure out how to do it
            delete localFilter.product;
        }

        if (localFilter.partner?.operator === ReportFilterOperator.IN && isEmpty(localFilter.partner?.value)) {
            delete localFilter.partner;
        }

        if (
            localFilter["items.product"]?.operator === ReportFilterOperator.IN &&
            isEmpty(localFilter["items.product"]?.value)
        ) {
            delete localFilter["items.product"];
        }

        if (
            localFilter["lineItems.coupon"]?.operator === ReportFilterOperator.IN &&
            isEmpty(localFilter["lineItems.coupon"]?.value)
        ) {
            delete localFilter["lineItems.coupon"];
        }

        if (report.object === ReportObject.TICKET_REDEMPTION && localFilter.createdAt) {
            localFilter.createdAt.value = formatCreatedAtEndDate(localFilter.createdAt.value as string[]);
        }

        if (localFilter?.arrivalTime) {
            localFilter.arrivalTime = {
                ...localFilter.arrivalTime,
                value: convertAnyStringToFormattedTime((localFilter?.arrivalTime.value as string).toString()), // TODO: FIX ME: I think we can just use `now()` here
            };
        }

        if (
            report.object === ReportObject.RESERVATIONS &&
            localFilter.status &&
            localFilter.status.operator === ReportFilterOperator.NIN
        ) {
            // Old saved reservations reports has NIN filter, now it is changed to IN filter, so removed old NIN
            delete localFilter.status;
        }

        if (report.object === ReportObject.RESERVATIONS_BY_LISTING && localFilter.arrivalDate) {
            // Filter arrival date by Item level also
            localFilter.items_arrivalDate = localFilter.arrivalDate;
        }

        if (keyToBeRemoved) {
            // Timeout needed for filter to be applied first
            setTimeout(() => {
                ref.current?.resetColumnFilter(keyToBeRemoved);
            }, 100);
        }

        setCurrentFilter(localFilter);
    };

    const expandedColumns = useMemo(() => {
        return getExpandedColumns(unwind?.(data) ?? data, columns);
    }, [data, columns, unwind]);

    const selectedColumns = useMemo(() => {
        const cols = expandedColumns.filter((col) => col.field?.length > 0 && !excludedColumns.includes(col.field));
        cols.splice(0, 0, numberColumn);
        return cols;
    }, [expandedColumns, excludedColumns]);

    const handleColumnChange = (excludedColumns: string[]) => {
        setExcludedColumns(excludedColumns);
    };

    const handleRunReport = () => {
        const pendingRangeKey = getPendingRangeFilterKey(currentFilter);
        if (pendingRangeKey) {
            const formattedString = pendingRangeKey
                .replace(/([A-Z])/g, " $1")
                .replace(/^./, (char) => char.toUpperCase());

            flash.danger(`An invalid value was specified for ${formattedString}`);
            return;
        }

        if (filter === currentFilter) {
            // Force run report even if the filter is not changed
            refetchReport();
            if (shouldFetchSummary) {
                refetchSummary();
            }
        } else {
            setFilter(currentFilter);
        }
    };

    const handleRowClicked = (event: any) => {
        const { data } = event;

        if (report.object === ReportObject.TRANSACTIONS) {
            if (
                isPartnerReconcileTransaction(data) ||
                (data.purchase && isPartnerPurchase(data.purchase) && !data.purchase.items)
            ) {
                // this is partner txn or only partner item purchase, so click should not redirect
                return;
            }

            if (data.type === "auto_payout" && data.id) {
                history.push(`/reports/payouts/${data.id}`);
            } else if ("purchase" in data) {
                const purchaseId = data.purchase.id;
                const itemId = data.object === PurchaseItemObject.EXPERIENCE ? data.id : null;
                const url = itemId ? `/purchases/${purchaseId}/${itemId}` : `/purchases/${purchaseId}`;
                if (purchaseId) {
                    history.push(url);
                }
            }
        }
    };

    const values = useMemo(() => unwind?.(data) ?? data, [data, unwind]);
    const hiddenFields =
        report.hiddenFields ??
        suggestedReports.find((r) => r.report.object === report.object)?.report.hiddenFields ??
        [];

    return (
        <>
            <div className="border-b border-gray-light">
                {expandedColumns && expandedColumns.length > 0 && (
                    <CustomReportFilter
                        isFetching={isFetching || isFetchingSummary}
                        report={report}
                        dateFields={dateFields}
                        isFilterChanged={currentFilter !== filter}
                        columns={expandedColumns}
                        excludedColumns={excludedColumns}
                        excludeFutureDates={excludeFutureDates}
                        onFilterChange={handleFilterChange}
                        onColumnChange={handleColumnChange}
                        onRun={handleRunReport}
                    />
                )}

                {/* Showing user readable representation of the filters */}
                <ReportFilterTagList
                    colDefs={expandedColumns}
                    value={currentFilter}
                    readOnlyFields={dateFields}
                    hiddenFields={hiddenFields}
                    onChange={handleFilterChange}
                />
            </div>

            <ColumnLayout.Fill>
                {(height) => (
                    <div className="py-2 px-6">
                        <CustomReportGrid
                            ref={ref}
                            height={height - 32 /* py-4 */}
                            report={report}
                            columnDefs={selectedColumns}
                            values={values}
                            count={summary?.count}
                            defaultFilter={initialFilter}
                            filter={currentFilter}
                            hasMore={hasNextPage}
                            isLoading={isLoading}
                            isLoadingMore={isFetchingNextPage}
                            onLoadMore={fetchNextPage}
                            onFilterChange={handleFilterChange}
                            onRowClicked={handleRowClicked}
                        />
                    </div>
                )}
            </ColumnLayout.Fill>
        </>
    );
};

const getExpandedColumns = (data: any[], columns: ReportColumnDef[]): ReportColumnDef[] => {
    const expandedColumns = cloneDeep(columns);
    const hasColumn = (headerName: string) => expandedColumns.some((column) => column.headerName === headerName);

    for (const column of columns) {
        const { expand } = column;
        if (expand) {
            const lineItemNames: string[] = uniq(get(data, `*.${expand.key}`, [])); // Get names of all the expanded columns
            const headerNames = expand?.headerGetter ? expand?.headerGetter(lineItemNames) : lineItemNames;
            for (const headerName of headerNames) {
                if (hasColumn(headerName) || !headerName) {
                    continue;
                }

                // Add this column to the schema
                const schema = {
                    ...expand,
                    headerName,
                    field: headerName.replace(/[^\w\-_]/, "").toLowerCase(), // Find anything that is NOT a word character, a dash, or an underscore and remove it
                    filter: false,
                    parent: pick(column, ["field", "headerName"]),
                };
                const pos = expandedColumns.findIndex((col) => col.headerName === column.headerName) + 1;
                expandedColumns.splice(pos, 0, schema as ReportColumnDef); // Add the dynamic column after the parent column
            }
        }
    }

    return expandedColumns;
};

const sortPurchaseItems = (item1: PurchaseItem, item2: PurchaseItem) => {
    if (isExperiencePurchaseItem(item1) && isExperiencePurchaseItem(item2)) {
        return item1.arrivalDateTime <= item2.arrivalDateTime ? -1 : 1;
    }

    if (isGiftPurchaseItem(item1) && !isGiftPurchaseItem(item2)) {
        return 1;
    }

    if (isGiftPurchaseItem(item2) && !isGiftPurchaseItem(item1)) {
        return -1;
    }

    return item1.id < item2.id ? -1 : 1;
};

const getPartnerFeeForItem = (data: PurchaseItem) => {
    const { lineItems } = data.purchase;
    const prePartnerFeeItemSubtotal = getLineItemByCode(data, LineItemCode.PRE_PARTNER_FEE_ITEM)?.amount ?? data.amount;
    const prePartnerFeeSubtotal =
        lineItems.find(({ code }) => code === LineItemCode.PRE_PARTNER_FEE)?.amount ?? data.purchase.amount;
    const partnerFee = lineItems.find(({ code }) => code === LineItemCode.PARTNER_FEES)?.amount ?? 0;

    if (!prePartnerFeeSubtotal || !partnerFee) {
        return 0;
    }

    return (prePartnerFeeItemSubtotal / prePartnerFeeSubtotal) * partnerFee;
};
